/**
 * 认证状态枚举
 *
 * 状态转换流程：
 * INITIALIZING → UNAUTHENTICATED
 *              → NEED_USER_SYNC → AUTHENTICATED / NEEDS_ONBOARDING
 *              → AUTH_ERROR
 */
export enum AuthState {
  /** Clerk SDK 正在加载 */
  INITIALIZING = 'initializing',

  /** 用户未登录 */
  UNAUTHENTICATED = 'unauthenticated',

  /** 正在验证用户状态（调用后端 verify-user） */
  NEED_USER_SYNC = 'need_user_sync',

  /** 需要完成组织设置（userStatus: pending_organization） */
  NEEDS_ONBOARDING = 'needs_onboarding',

  /** 完全认证，可以访问系统（userStatus: active） */
  AUTHENTICATED = 'authenticated',

  /** 认证过程出错 */
  AUTH_ERROR = 'auth_error',
}

/**
 * 认证上下文接口
 */
export interface AuthContextValue {
  /** 当前认证状态 */
  state: AuthState

  /** Clerk 用户对象 */
  user: any | null

  /** 错误信息 */
  error?: string

  /** 是否为可恢复错误（用于决定是否显示 Retry 按钮） */
  isRecoverableError?: boolean

  /** 完成 onboarding */
  completeOnboarding: (data: OnboardingData) => Promise<void>

  /** 退出登录 */
  signOut: () => Promise<void>

  /** 刷新认证状态 */
  refreshAuth: () => Promise<void>

  /** Clerk JS 加载失败（仅 SHOW_LEGACY_LOGIN 模式下可能为 true） */
  clerkLoadFailed: boolean
}

/**
 * Onboarding 数据
 */
export interface OnboardingData {
  /** 创建新公司 */
  companyName?: string

  /** 加入现有公司 */
  accessCode?: string
}

/**
 * Clerk Public Metadata 结构
 */
export interface ClerkPublicMetadata {
  dbUserId?: string
  dbTenantId?: string
}

/**
 * 用户状态枚举（与后端对应）
 */
export enum UserStatus {
  /** 正常活跃用户 - 已完成所有必要步骤，可以正常使用系统 */
  ACTIVE = 'active',
  /** 待加入组织 - 需要创建新组织或加入现有组织 */
  PENDING_ORGANIZATION = 'pending_organization',
  /** 账号未激活 - 账号已创建但未激活，无法登录 */
  INACTIVE = 'inactive',
}

/**
 * Provider 同步状态枚举（与后端对应）
 */
export enum ProviderSyncStatus {
  /** 数据已同步更新 - Provider 的用户数据（如 metadata）已成功更新，前端应刷新 token */
  SYNCED = 'synced',
  /** 无需同步 - Provider 数据与数据库已一致，无需更新 */
  NOT_NEEDED = 'not_needed',
  /** 同步失败 - 尝试更新 Provider 数据失败，但不影响核心功能 */
  FAILED = 'failed',
}

/**
 * verify-user 接口响应
 */
export interface VerifyUserResponse {
  userStatus: UserStatus
  providerSyncStatus: ProviderSyncStatus
}
